import React, { useState } from 'react';
import { CalculatorIcon, TrendingUpIcon, DollarSignIcon, RefreshCwIcon, ArrowRightIcon } from 'lucide-react';
export function ROICalculator() {
  // Default values
  const defaults = {
    laborHours: 80,
    hourlyRate: 50,
    monthlyLeads: 10,
    conversionRate: 10,
    ltv: 5000,
    hourReduction: 60,
    leadIncrease: 40,
    conversionImprovement: 15,
    implementationCost: 2000,
    monthlyCost: 500
  };
  const [inputs, setInputs] = useState(defaults);
  const handleInputChange = (field: string, value: number) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const resetToDefaults = () => {
    setInputs(defaults);
  };
  const resetAutomationImpact = () => {
    setInputs(prev => ({
      ...prev,
      hourReduction: defaults.hourReduction,
      leadIncrease: defaults.leadIncrease,
      conversionImprovement: defaults.conversionImprovement,
      implementationCost: defaults.implementationCost,
      monthlyCost: defaults.monthlyCost
    }));
  };
  // Calculations
  const calculations = {
    // Cost Savings
    hoursSaved: inputs.laborHours * inputs.hourReduction / 100,
    monthlySavings: inputs.laborHours * inputs.hourlyRate * inputs.hourReduction / 100,
    annualLaborSavings: inputs.laborHours * inputs.hourlyRate * inputs.hourReduction * 12 / 100,
    // Revenue Impact
    additionalLeads: inputs.monthlyLeads * inputs.leadIncrease / 100,
    currentCustomers: inputs.monthlyLeads * inputs.conversionRate / 100,
    newConversionRate: inputs.conversionRate * (1 + inputs.conversionImprovement / 100),
    additionalCustomersFromLeads: inputs.monthlyLeads * inputs.leadIncrease / 100 * (inputs.conversionRate / 100),
    additionalCustomersFromConversion: inputs.monthlyLeads * (inputs.conversionRate * (1 + inputs.conversionImprovement / 100) / 100 - inputs.conversionRate / 100),
    // Current state
    currentAnnualLabor: inputs.laborHours * inputs.hourlyRate * 12,
    currentAnnualRevenue: inputs.monthlyLeads * inputs.conversionRate / 100 * inputs.ltv * 12
  };
  calculations.additionalMonthlyCustomers = calculations.additionalCustomersFromLeads + calculations.additionalCustomersFromConversion;
  calculations.additionalMonthlyRevenue = calculations.additionalMonthlyCustomers * inputs.ltv;
  calculations.annualRevenueIncrease = calculations.additionalMonthlyRevenue * 12;
  // P&L calculations
  calculations.automatedAnnualLabor = calculations.currentAnnualLabor - calculations.annualLaborSavings;
  calculations.automatedAnnualRevenue = calculations.currentAnnualRevenue + calculations.annualRevenueIncrease;
  calculations.annualAutomationCost = inputs.monthlyCost * 12;
  calculations.currentNetProfit = calculations.currentAnnualRevenue - calculations.currentAnnualLabor;
  calculations.automatedNetProfit = calculations.automatedAnnualRevenue - calculations.automatedAnnualLabor - calculations.annualAutomationCost;
  calculations.netAnnualProfitImpact = calculations.automatedNetProfit - calculations.currentNetProfit;
  // ROI Metrics
  calculations.firstYearInvestment = inputs.implementationCost + calculations.annualAutomationCost;
  calculations.firstYearBenefit = calculations.annualLaborSavings + calculations.annualRevenueIncrease - calculations.firstYearInvestment;
  calculations.roiPercent = calculations.firstYearBenefit / calculations.firstYearInvestment * 100;
  calculations.paybackMonths = calculations.firstYearInvestment / (calculations.monthlySavings + calculations.additionalMonthlyRevenue - inputs.monthlyCost);
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  const formatNumber = (value: number, decimals: number = 1) => {
    return value.toFixed(decimals);
  };
  return <div className="w-full bg-zinc-950 py-24 relative overflow-hidden" data-id="element-441">
      <div className="absolute inset-0 opacity-5 animate-diagonal-move" style={{
      backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, #ffa500 10px, #ffa500 11px)'
    }} data-id="element-442" />
      {/* Floating particles */}
      {[...Array(30)].map((_, i) => <div key={i} className="absolute rounded-full animate-float" style={{
      width: `${2 + Math.random() * 4}px`,
      height: `${2 + Math.random() * 4}px`,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      backgroundColor: i % 2 === 0 ? '#22c55e' : '#f59e0b',
      animationDelay: `${Math.random() * 5}s`,
      animationDuration: `${10 + Math.random() * 15}s`,
      opacity: 0.3
    }} data-id="element-443" />)}
      <div className="container mx-auto px-6 relative z-10" data-id="element-444">
        <div className="max-w-7xl mx-auto" data-id="element-445">
          {/* Header */}
          <div className="mb-12 text-center animate-fade-in-up" data-id="element-446">
            <div className="inline-block border-4 border-amber-500 bg-black px-6 py-3 mb-6 shadow-[0_0_40px_rgba(255,165,0,0.4)] animate-glow-amber" data-id="element-447">
              <h2 className="text-4xl md:text-5xl font-bold text-amber-500 font-mono flex items-center gap-3 justify-center" data-id="element-448">
                <CalculatorIcon className="w-10 h-10 animate-pulse" data-id="element-449" />
                AI AUTOMATION ROI CALCULATOR
              </h2>
            </div>
            <p className="text-green-400 text-xl font-mono mb-4 animate-fade-in-up" style={{
            animationDelay: '0.2s'
          }} data-id="element-450">
              // CALCULATE YOUR PROFIT IMPACT IN REAL-TIME
            </p>
            <p className="text-amber-100 text-lg max-w-3xl mx-auto animate-fade-in-up" style={{
            animationDelay: '0.3s'
          }} data-id="element-451">
              See exactly how AI automation affects your P&L statement. Adjust
              the inputs below to model your specific situation.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 mb-12" data-id="element-452">
            {/* Input Section */}
            <div className="space-y-6" data-id="element-453">
              {/* Current State */}
              <div className="border-4 border-green-500 bg-black p-6 animate-fade-in-up hover:shadow-[0_0_40px_rgba(0,255,0,0.3)] transition-all duration-500 group relative overflow-hidden" style={{
              animationDelay: '0.4s'
            }} data-id="element-454">
                <div className="absolute inset-0 bg-gradient-to-br from-green-950/0 via-green-950/10 to-green-950/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-455" />
                <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-green-400 animate-pulse" data-id="element-456" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-green-400 animate-pulse" style={{
                animationDelay: '0.5s'
              }} data-id="element-457" />
                <div className="border-b-2 border-green-500 pb-3 mb-6 relative z-10" data-id="element-458">
                  <h3 className="text-2xl font-bold text-green-400 font-mono flex items-center justify-between" data-id="element-459">
                    CURRENT STATE
                    <button onClick={resetToDefaults} className="text-sm px-3 py-1 border-2 border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black transition-all duration-300 flex items-center gap-2 hover:scale-105 hover:shadow-[0_0_20px_rgba(255,165,0,0.5)]" data-id="element-460">
                      <RefreshCwIcon className="w-4 h-4" data-id="element-461" />
                      RESET
                    </button>
                  </h3>
                </div>
                <div className="space-y-5 relative z-10" data-id="element-462">
                  <div className="hover:translate-x-2 transition-transform duration-300" data-id="element-463">
                    <label className="text-amber-500 font-mono text-sm mb-2 block" data-id="element-464">
                      Monthly Labor Hours on Manual Processes
                    </label>
                    <input type="number" value={inputs.laborHours} onChange={e => handleInputChange('laborHours', Number(e.target.value))} className="w-full bg-zinc-900 border-2 border-amber-600 text-amber-100 px-4 py-2 font-mono focus:outline-none focus:border-amber-400 focus:shadow-[0_0_20px_rgba(255,165,0,0.3)] transition-all duration-300" data-id="element-465" />
                  </div>
                  <div className="hover:translate-x-2 transition-transform duration-300" data-id="element-466">
                    <label className="text-amber-500 font-mono text-sm mb-2 block" data-id="element-467">
                      Average Hourly Labor Cost ($)
                    </label>
                    <input type="number" value={inputs.hourlyRate} onChange={e => handleInputChange('hourlyRate', Number(e.target.value))} className="w-full bg-zinc-900 border-2 border-amber-600 text-amber-100 px-4 py-2 font-mono focus:outline-none focus:border-amber-400 focus:shadow-[0_0_20px_rgba(255,165,0,0.3)] transition-all duration-300" data-id="element-468" />
                  </div>
                  <div className="hover:translate-x-2 transition-transform duration-300" data-id="element-469">
                    <label className="text-amber-500 font-mono text-sm mb-2 block" data-id="element-470">
                      Current Monthly Leads Generated
                    </label>
                    <input type="number" value={inputs.monthlyLeads} onChange={e => handleInputChange('monthlyLeads', Number(e.target.value))} className="w-full bg-zinc-900 border-2 border-amber-600 text-amber-100 px-4 py-2 font-mono focus:outline-none focus:border-amber-400 focus:shadow-[0_0_20px_rgba(255,165,0,0.3)] transition-all duration-300" data-id="element-471" />
                  </div>
                  <div className="hover:translate-x-2 transition-transform duration-300" data-id="element-472">
                    <label className="text-amber-500 font-mono text-sm mb-2 block" data-id="element-473">
                      Lead-to-Customer Conversion Rate: {inputs.conversionRate}%
                    </label>
                    <input type="range" min="0" max="100" value={inputs.conversionRate} onChange={e => handleInputChange('conversionRate', Number(e.target.value))} className="w-full h-2 bg-zinc-900 appearance-none cursor-pointer slider-amber" data-id="element-474" />
                  </div>
                  <div className="hover:translate-x-2 transition-transform duration-300" data-id="element-475">
                    <label className="text-amber-500 font-mono text-sm mb-2 block" data-id="element-476">
                      Average Customer Lifetime Value ($)
                    </label>
                    <input type="number" value={inputs.ltv} onChange={e => handleInputChange('ltv', Number(e.target.value))} className="w-full bg-zinc-900 border-2 border-amber-600 text-amber-100 px-4 py-2 font-mono focus:outline-none focus:border-amber-400 focus:shadow-[0_0_20px_rgba(255,165,0,0.3)] transition-all duration-300" data-id="element-477" />
                  </div>
                </div>
              </div>
              {/* Automation Impact */}
              <div className="border-4 border-amber-600 bg-black p-6 animate-fade-in-up hover:shadow-[0_0_40px_rgba(255,165,0,0.3)] transition-all duration-500 group relative overflow-hidden" style={{
              animationDelay: '0.5s'
            }} data-id="element-478">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-950/0 via-amber-950/10 to-amber-950/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-479" />
                <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-amber-400 animate-pulse" data-id="element-480" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-amber-400 animate-pulse" style={{
                animationDelay: '0.5s'
              }} data-id="element-481" />
                <div className="border-b-2 border-amber-600 pb-3 mb-6 relative z-10" data-id="element-482">
                  <h3 className="text-2xl font-bold text-amber-500 font-mono flex items-center justify-between" data-id="element-483">
                    AUTOMATION IMPACT
                    <button onClick={resetAutomationImpact} className="text-sm px-3 py-1 border-2 border-green-500 text-green-400 hover:bg-green-500 hover:text-black transition-all duration-300 flex items-center gap-2 hover:scale-105 hover:shadow-[0_0_20px_rgba(0,255,0,0.5)]" data-id="element-484">
                      <RefreshCwIcon className="w-4 h-4" data-id="element-485" />
                      RESET
                    </button>
                  </h3>
                </div>
                <div className="space-y-5 relative z-10" data-id="element-486">
                  <div className="hover:translate-x-2 transition-transform duration-300" data-id="element-487">
                    <label className="text-green-400 font-mono text-sm mb-2 block" data-id="element-488">
                      Expected Reduction in Manual Hours: {inputs.hourReduction}
                      %
                    </label>
                    <input type="range" min="0" max="100" value={inputs.hourReduction} onChange={e => handleInputChange('hourReduction', Number(e.target.value))} className="w-full h-2 bg-zinc-900 appearance-none cursor-pointer slider-green" data-id="element-489" />
                  </div>
                  <div className="hover:translate-x-2 transition-transform duration-300" data-id="element-490">
                    <label className="text-green-400 font-mono text-sm mb-2 block" data-id="element-491">
                      Expected Increase in Monthly Leads: {inputs.leadIncrease}%
                    </label>
                    <input type="range" min="0" max="100" value={inputs.leadIncrease} onChange={e => handleInputChange('leadIncrease', Number(e.target.value))} className="w-full h-2 bg-zinc-900 appearance-none cursor-pointer slider-green" data-id="element-492" />
                  </div>
                  <div className="hover:translate-x-2 transition-transform duration-300" data-id="element-493">
                    <label className="text-green-400 font-mono text-sm mb-2 block" data-id="element-494">
                      Expected Improvement in Conversion Rate:{' '}
                      {inputs.conversionImprovement}%
                    </label>
                    <input type="range" min="0" max="100" value={inputs.conversionImprovement} onChange={e => handleInputChange('conversionImprovement', Number(e.target.value))} className="w-full h-2 bg-zinc-900 appearance-none cursor-pointer slider-green" data-id="element-495" />
                  </div>
                  <div className="hover:translate-x-2 transition-transform duration-300" data-id="element-496">
                    <label className="text-green-400 font-mono text-sm mb-2 block" data-id="element-497">
                      One-Time Implementation Cost ($)
                    </label>
                    <input type="number" value={inputs.implementationCost} onChange={e => handleInputChange('implementationCost', Number(e.target.value))} className="w-full bg-zinc-900 border-2 border-green-500 text-amber-100 px-4 py-2 font-mono focus:outline-none focus:border-green-400 focus:shadow-[0_0_20px_rgba(0,255,0,0.3)] transition-all duration-300" data-id="element-498" />
                  </div>
                  <div className="hover:translate-x-2 transition-transform duration-300" data-id="element-499">
                    <label className="text-green-400 font-mono text-sm mb-2 block" data-id="element-500">
                      Monthly Automation Cost ($)
                    </label>
                    <input type="number" value={inputs.monthlyCost} onChange={e => handleInputChange('monthlyCost', Number(e.target.value))} className="w-full bg-zinc-900 border-2 border-green-500 text-amber-100 px-4 py-2 font-mono focus:outline-none focus:border-green-400 focus:shadow-[0_0_20px_rgba(0,255,0,0.3)] transition-all duration-300" data-id="element-501" />
                  </div>
                </div>
              </div>
            </div>
            {/* Results Section */}
            <div className="flex flex-col justify-center space-y-6" data-id="element-502">
              {/* Cost Savings */}
              <div className="border-4 border-green-500 bg-black p-8 w-full animate-fade-in-up hover:shadow-[0_0_50px_rgba(0,255,0,0.4)] hover:scale-[1.02] transition-all duration-500 group relative overflow-hidden" style={{
              animationDelay: '0.6s'
            }} data-id="element-503">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-500/10 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" data-id="element-504" />
                <div className="absolute top-2 left-2 w-6 h-6 border-t-2 border-l-2 border-green-400 animate-pulse" data-id="element-505" />
                <div className="absolute bottom-2 right-2 w-6 h-6 border-b-2 border-r-2 border-green-400 animate-pulse" style={{
                animationDelay: '0.5s'
              }} data-id="element-506" />
                <div className="border-b-2 border-green-500 pb-4 mb-6 relative z-10" data-id="element-507">
                  <h3 className="text-2xl font-bold text-green-400 font-mono flex items-center gap-3 group-hover:text-glow transition-all duration-300" data-id="element-508">
                    <TrendingUpIcon className="w-8 h-8 animate-pulse" data-id="element-509" />
                    COST SAVINGS
                  </h3>
                </div>
                <div className="space-y-5 relative z-10" data-id="element-510">
                  <div className="flex justify-between items-center hover:translate-x-2 transition-transform duration-300" data-id="element-511">
                    <span className="text-amber-100 text-base" data-id="element-512">
                      Monthly Hours Saved:
                    </span>
                    <span className="text-green-400 font-mono font-bold text-xl animate-pulse" data-id="element-513">
                      {formatNumber(calculations.hoursSaved)} hrs
                    </span>
                  </div>
                  <div className="flex justify-between items-center hover:translate-x-2 transition-transform duration-300" data-id="element-514">
                    <span className="text-amber-100 text-base" data-id="element-515">
                      Monthly Labor Savings:
                    </span>
                    <span className="text-green-400 font-mono font-bold text-xl animate-pulse" data-id="element-516">
                      {formatCurrency(calculations.monthlySavings)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-4 border-t-2 border-green-500 hover:translate-x-2 transition-transform duration-300" data-id="element-517">
                    <span className="text-amber-100 font-semibold text-lg" data-id="element-518">
                      Annual Labor Savings:
                    </span>
                    <span className="text-green-400 font-mono font-bold text-2xl group-hover:text-glow transition-all duration-300" data-id="element-519">
                      {formatCurrency(calculations.annualLaborSavings)}
                    </span>
                  </div>
                </div>
              </div>
              {/* Revenue Impact */}
              <div className="border-4 border-amber-600 bg-black p-8 w-full animate-fade-in-up hover:shadow-[0_0_50px_rgba(255,165,0,0.4)] hover:scale-[1.02] transition-all duration-500 group relative overflow-hidden" style={{
              animationDelay: '0.7s'
            }} data-id="element-520">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/10 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" data-id="element-521" />
                <div className="absolute top-2 left-2 w-6 h-6 border-t-2 border-l-2 border-amber-400 animate-pulse" data-id="element-522" />
                <div className="absolute bottom-2 right-2 w-6 h-6 border-b-2 border-r-2 border-amber-400 animate-pulse" style={{
                animationDelay: '0.5s'
              }} data-id="element-523" />
                <div className="border-b-2 border-amber-600 pb-4 mb-6 relative z-10" data-id="element-524">
                  <h3 className="text-2xl font-bold text-amber-500 font-mono flex items-center gap-3 group-hover:text-glow transition-all duration-300" data-id="element-525">
                    <DollarSignIcon className="w-8 h-8 animate-pulse" data-id="element-526" />
                    REVENUE IMPACT
                  </h3>
                </div>
                <div className="space-y-5 relative z-10" data-id="element-527">
                  <div className="flex justify-between items-center hover:translate-x-2 transition-transform duration-300" data-id="element-528">
                    <span className="text-amber-100 text-base" data-id="element-529">
                      Additional Monthly Leads:
                    </span>
                    <span className="text-green-400 font-mono font-bold text-xl animate-pulse" data-id="element-530">
                      +{formatNumber(calculations.additionalLeads, 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center hover:translate-x-2 transition-transform duration-300" data-id="element-531">
                    <span className="text-amber-100 text-base" data-id="element-532">
                      Additional Monthly Customers:
                    </span>
                    <span className="text-green-400 font-mono font-bold text-xl animate-pulse" data-id="element-533">
                      +{formatNumber(calculations.additionalMonthlyCustomers)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center hover:translate-x-2 transition-transform duration-300" data-id="element-534">
                    <span className="text-amber-100 text-base" data-id="element-535">
                      Additional Monthly Revenue:
                    </span>
                    <span className="text-green-400 font-mono font-bold text-xl animate-pulse" data-id="element-536">
                      {formatCurrency(calculations.additionalMonthlyRevenue)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-4 border-t-2 border-amber-600 hover:translate-x-2 transition-transform duration-300" data-id="element-537">
                    <span className="text-amber-100 font-semibold text-lg" data-id="element-538">
                      Annual Revenue Increase:
                    </span>
                    <span className="text-green-400 font-mono font-bold text-2xl group-hover:text-glow transition-all duration-300" data-id="element-539">
                      {formatCurrency(calculations.annualRevenueIncrease)}
                    </span>
                  </div>
                </div>
              </div>
              {/* ROI Metrics */}
              <div className="border-4 border-green-500 bg-black p-8 w-full animate-fade-in-up hover:shadow-[0_0_50px_rgba(0,255,0,0.4)] hover:scale-[1.02] transition-all duration-500 group relative overflow-hidden" style={{
              animationDelay: '0.8s'
            }} data-id="element-540">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-500/10 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" data-id="element-541" />
                <div className="absolute top-2 left-2 w-6 h-6 border-t-2 border-l-2 border-green-400 animate-pulse" data-id="element-542" />
                <div className="absolute bottom-2 right-2 w-6 h-6 border-b-2 border-r-2 border-green-400 animate-pulse" style={{
                animationDelay: '0.5s'
              }} data-id="element-543" />
                <div className="border-b-2 border-green-500 pb-4 mb-6 relative z-10" data-id="element-544">
                  <h3 className="text-2xl font-bold text-green-400 font-mono group-hover:text-glow transition-all duration-300" data-id="element-545">
                    FIRST YEAR ROI
                  </h3>
                </div>
                <div className="space-y-5 relative z-10" data-id="element-546">
                  <div className="flex justify-between items-center hover:translate-x-2 transition-transform duration-300" data-id="element-547">
                    <span className="text-amber-100 text-base" data-id="element-548">
                      Total Investment:
                    </span>
                    <span className="text-red-400 font-mono font-bold text-xl" data-id="element-549">
                      {formatCurrency(calculations.firstYearInvestment)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center hover:translate-x-2 transition-transform duration-300" data-id="element-550">
                    <span className="text-amber-100 text-base" data-id="element-551">
                      Net Benefit:
                    </span>
                    <span className={`font-mono font-bold text-xl animate-pulse ${calculations.firstYearBenefit >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-552">
                      {formatCurrency(calculations.firstYearBenefit)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center hover:translate-x-2 transition-transform duration-300" data-id="element-553">
                    <span className="text-amber-100 text-base" data-id="element-554">
                      ROI Percentage:
                    </span>
                    <span className={`font-mono font-bold text-xl animate-pulse ${calculations.roiPercent >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-555">
                      {formatNumber(calculations.roiPercent, 0)}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-4 border-t-2 border-green-500 hover:translate-x-2 transition-transform duration-300" data-id="element-556">
                    <span className="text-amber-100 font-semibold text-lg" data-id="element-557">
                      Payback Period:
                    </span>
                    <span className="text-green-400 font-mono font-bold text-2xl group-hover:text-glow transition-all duration-300" data-id="element-558">
                      {formatNumber(calculations.paybackMonths, 1)} months
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* P&L Comparison Table */}
          <div className="border-4 border-amber-600 bg-black p-8 animate-fade-in-up hover:shadow-[0_0_40px_rgba(255,165,0,0.3)] transition-all duration-500 group relative overflow-hidden" style={{
          animationDelay: '0.9s'
        }} data-id="element-559">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-950/0 via-amber-950/10 to-amber-950/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-560" />
            <div className="absolute top-2 left-2 w-8 h-8 border-t-2 border-l-2 border-amber-400 animate-pulse" data-id="element-561" />
            <div className="absolute bottom-2 right-2 w-8 h-8 border-b-2 border-r-2 border-amber-400 animate-pulse" style={{
            animationDelay: '0.5s'
          }} data-id="element-562" />
            <div className="border-b-2 border-amber-600 pb-4 mb-6 relative z-10" data-id="element-563">
              <h3 className="text-2xl font-bold text-amber-500 font-mono group-hover:text-glow transition-all duration-300" data-id="element-564">
                ANNUAL P&L COMPARISON
              </h3>
            </div>
            <div className="overflow-x-auto relative z-10" data-id="element-565">
              <table className="w-full text-left" data-id="element-566">
                <thead data-id="element-567">
                  <tr className="border-b-2 border-amber-600" data-id="element-568">
                    <th className="py-3 px-4 text-green-400 font-mono" data-id="element-569">
                      METRIC
                    </th>
                    <th className="py-3 px-4 text-green-400 font-mono text-right" data-id="element-570">
                      CURRENT
                    </th>
                    <th className="py-3 px-4 text-green-400 font-mono text-right" data-id="element-571">
                      WITH AUTOMATION
                    </th>
                    <th className="py-3 px-4 text-green-400 font-mono text-right" data-id="element-572">
                      IMPACT
                    </th>
                  </tr>
                </thead>
                <tbody className="font-mono" data-id="element-573">
                  <tr className="border-b border-zinc-700 hover:bg-zinc-900 transition-colors duration-300" data-id="element-574">
                    <td className="py-3 px-4 text-amber-100" data-id="element-575">
                      Annual Labor Costs
                    </td>
                    <td className="py-3 px-4 text-right text-red-400" data-id="element-576">
                      {formatCurrency(calculations.currentAnnualLabor)}
                    </td>
                    <td className="py-3 px-4 text-right text-red-400" data-id="element-577">
                      {formatCurrency(calculations.automatedAnnualLabor)}
                    </td>
                    <td className="py-3 px-4 text-right text-green-400 font-bold" data-id="element-578">
                      -{formatCurrency(calculations.annualLaborSavings)}
                    </td>
                  </tr>
                  <tr className="border-b border-zinc-700 hover:bg-zinc-900 transition-colors duration-300" data-id="element-579">
                    <td className="py-3 px-4 text-amber-100" data-id="element-580">Annual Revenue</td>
                    <td className="py-3 px-4 text-right text-green-400" data-id="element-581">
                      {formatCurrency(calculations.currentAnnualRevenue)}
                    </td>
                    <td className="py-3 px-4 text-right text-green-400" data-id="element-582">
                      {formatCurrency(calculations.automatedAnnualRevenue)}
                    </td>
                    <td className="py-3 px-4 text-right text-green-400 font-bold" data-id="element-583">
                      +{formatCurrency(calculations.annualRevenueIncrease)}
                    </td>
                  </tr>
                  <tr className="border-b border-zinc-700 hover:bg-zinc-900 transition-colors duration-300" data-id="element-584">
                    <td className="py-3 px-4 text-amber-100" data-id="element-585">
                      Annual Automation Costs
                    </td>
                    <td className="py-3 px-4 text-right text-amber-100" data-id="element-586">$0</td>
                    <td className="py-3 px-4 text-right text-red-400" data-id="element-587">
                      {formatCurrency(calculations.annualAutomationCost)}
                    </td>
                    <td className="py-3 px-4 text-right text-red-400 font-bold" data-id="element-588">
                      -{formatCurrency(calculations.annualAutomationCost)}
                    </td>
                  </tr>
                  <tr className="border-t-2 border-amber-600 bg-amber-950/20 hover:bg-amber-950/30 transition-colors duration-300" data-id="element-589">
                    <td className="py-4 px-4 text-amber-500 font-bold" data-id="element-590">
                      Net Annual Profit
                    </td>
                    <td className="py-4 px-4 text-right text-amber-100 font-bold text-lg" data-id="element-591">
                      {formatCurrency(calculations.currentNetProfit)}
                    </td>
                    <td className="py-4 px-4 text-right text-amber-100 font-bold text-lg" data-id="element-592">
                      {formatCurrency(calculations.automatedNetProfit)}
                    </td>
                    <td className={`py-4 px-4 text-right font-bold text-xl animate-pulse ${calculations.netAnnualProfitImpact >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-593">
                      {calculations.netAnnualProfitImpact >= 0 ? '+' : ''}
                      {formatCurrency(calculations.netAnnualProfitImpact)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          {/* Bottom note */}
          <div className="mt-8 border-4 border-green-500 bg-black p-6 text-center animate-fade-in-up hover:shadow-[0_0_30px_rgba(0,255,0,0.3)] transition-all duration-500 relative overflow-hidden group" style={{
          animationDelay: '1s'
        }} data-id="element-594">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-500/10 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" data-id="element-595" />
            <p className="text-amber-100 text-lg mb-2 relative z-10" data-id="element-596">
              These calculations show potential impact based on your inputs.
            </p>
            <p className="text-green-400 font-mono text-sm mb-6 relative z-10 group-hover:text-glow transition-all duration-300" data-id="element-597">
              // READY TO MAKE THIS REAL? LET'S TALK.
            </p>
            <a href="https://www.linkedin.com/in/dawsonsewell" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-500 text-black font-bold py-4 px-8 transition-all duration-300 border-4 border-amber-400 shadow-[0_0_30px_rgba(255,165,0,0.6)] hover:shadow-[0_0_50px_rgba(255,165,0,0.9)] group/btn text-lg relative z-10 animate-pulse-cta hover:scale-105" data-id="element-598">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000" data-id="element-599" />
              <span className="tracking-wider relative z-10" data-id="element-600">
                CONNECT ON LINKEDIN
              </span>
              <ArrowRightIcon className="w-5 h-5 group-hover/btn:translate-x-2 transition-transform duration-300 relative z-10" data-id="element-601" />
            </a>
          </div>
        </div>
      </div>
      <style data-id="element-602">{`
        @keyframes diagonal-move {
          0% { transform: translate(0, 0); }
          100% { transform: translate(20px, 20px); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0) rotate(0deg); }
          50% { transform: translateY(-120px) translateX(60px) rotate(180deg); }
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes glow-amber {
          0%, 100% { box-shadow: 0 0 40px rgba(255, 165, 0, 0.4); }
          50% { box-shadow: 0 0 60px rgba(255, 165, 0, 0.7); }
        }
        .animate-diagonal-move {
          animation: diagonal-move 15s linear infinite;
        }
        .animate-float {
          animation: float linear infinite;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
        }
        .animate-glow-amber {
          animation: glow-amber 3s ease-in-out infinite;
        }
        .text-glow {
          text-shadow: 0 0 20px currentColor;
        }
        .slider-amber::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          background: #f59e0b;
          cursor: pointer;
          border: 2px solid #fbbf24;
          transition: all 0.3s;
        }
        .slider-amber::-webkit-slider-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 0 20px rgba(255, 165, 0, 0.6);
        }
        .slider-amber::-moz-range-thumb {
          width: 20px;
          height: 20px;
          background: #f59e0b;
          cursor: pointer;
          border: 2px solid #fbbf24;
          transition: all 0.3s;
        }
        .slider-amber::-moz-range-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 0 20px rgba(255, 165, 0, 0.6);
        }
        .slider-green::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          background: #22c55e;
          cursor: pointer;
          border: 2px solid #4ade80;
          transition: all 0.3s;
        }
        .slider-green::-webkit-slider-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 0 20px rgba(0, 255, 0, 0.6);
        }
        .slider-green::-moz-range-thumb {
          width: 20px;
          height: 20px;
          background: #22c55e;
          cursor: pointer;
          border: 2px solid #4ade80;
          transition: all 0.3s;
        }
        .slider-green::-moz-range-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 0 20px rgba(0, 255, 0, 0.6);
        }
        .slider-amber::-webkit-slider-track {
          background: #18181b;
          border: 2px solid #f59e0b;
        }
        .slider-green::-webkit-slider-track {
          background: #18181b;
          border: 2px solid #22c55e;
        }
      `}</style>
    </div>;
}